import React, { createContext, useState, useContext, useEffect } from 'react';
import { toZonedTime } from 'date-fns-tz';
import { startOfDay } from 'date-fns';
import PropTypes from 'prop-types';

import { useTimezone } from '../../store/slices/commonSlice';
import { useUserDetails } from '../../store/slices/authSlice';

const CallingContext = createContext();

export const useCallingData = () => {
  return useContext(CallingContext);
};

export const CallingContextProvider = ({ children }) => {
  const timezone = useTimezone();
  const user = useUserDetails();
  const today = toZonedTime(new Date(), timezone);
  const [informationData, setInformationData] = useState({});
  const [dispositionsData, setDispositionsData] = useState({});
  const [fieldsData, setFieldsData] = useState({});
  const [isPauseMode, setIsPauseMode] = useState(false);
  const [resetTimer, setResetTimer] = useState(0);
  const [mutedConference, setMutedConference] = useState([]);
  const [onHoldConference, setOnHoldConference] = useState([]);
  const [micPermission, setMicPermission] = useState('checking');
  const [showMicModal, setShowMicModal] = useState(false);
  const [followUp, setFollowUp] = useState({
    date_and_time: startOfDay(today),
    isFollowUp: 'no'
  });

  const checkMicrophonePermission = async () => {
    try {
      if (!navigator.permissions) {
        setShowMicModal(true);
        return;
      }

      const result = await navigator.permissions.query({ name: 'microphone' });

      setMicPermission(result.state);

      if (result.state !== 'granted') {
        setShowMicModal(true);
      }
    } catch (error) {
      console.error('Permission check failed:', error);
      setShowMicModal(true);
    }
  };

  const requestMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      stream.getTracks().forEach((track) => track.stop());

      setMicPermission('granted');
      setShowMicModal(false);

      return true;
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        setMicPermission('denied');
        setShowMicModal(true);
      }
      return false;
    }
  };

  useEffect(() => {
    if (user && Number(user.web_phone) === 0) {
      checkMicrophonePermission();
    } else {
      setShowMicModal(false);
    }
  }, [user]);

  const handleTimeReset = () => {
    setResetTimer((prev) => prev + 1);
  };

  return (
    <CallingContext.Provider
      value={{
        informationData,
        dispositionsData,
        fieldsData,
        followUp,
        isPauseMode,
        resetTimer,
        mutedConference,
        onHoldConference,
        micPermission,
        showMicModal,
        setShowMicModal,
        checkMicrophonePermission,
        requestMicrophonePermission,
        setInformationData,
        setDispositionsData,
        setFieldsData,
        setFollowUp,
        setIsPauseMode,
        setResetTimer,
        handleTimeReset,
        setMutedConference,
        setOnHoldConference
      }}>
      {children}
    </CallingContext.Provider>
  );
};

CallingContextProvider.propTypes = {
  children: PropTypes.node.isRequired
};
